import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { conversationAction } from 'actions';
import DeleteModal from 'components/Modal/DeleteModal';
import EditModal from 'components/Modal/EditModal';
import { TEMPORARY } from 'helpers/constant';
import { groupByDateBucket, relativeTimeFormat } from 'helpers/date';
import { VIEW_MODE } from 'helpers/enum';
import {
  MessageSquare,
  MoreHorizontal,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Search,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Conversation } from 'types/reducers/conversation';

interface ConversationListProps {
  onAddNew: () => void;
  onChange: (_conversation: Conversation) => void;
  onCloseSidebar: () => void;
}

const ConversationsList = ({ onAddNew, onChange, onCloseSidebar }: ConversationListProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { conversations, current, selectedCitation, viewMode, processingStreamingChunkStatus } =
    useSelector((state) => state.conversations);
  const { data, total_page, current_page } = conversations;

  const [recordDelete, setRecordDelete] = useState<Conversation>();
  const [recordEdit, setRecordEdit] = useState<Conversation>();
  const [searchTerm, setSearchTerm] = useState('');

  const { isOpen: isToggleSidebar, onToggle: onToggleSidebar } = useDisclosure({
    defaultIsOpen: true,
  });
  const {
    isOpen: isOpenEditDialog,
    onOpen: onOpenEditDialog,
    onClose: onCloseEditDialog,
  } = useDisclosure();
  const {
    isOpen: isOpenDeleteDialog,
    onOpen: onOpenDeleteDialog,
    onClose: onCloseDeleteDialog,
  } = useDisclosure();

  const isShowCitation = Boolean(viewMode === VIEW_MODE.PREVIEW_FILE && selectedCitation?.file_url);
  const isShow = isToggleSidebar && !isShowCitation;
  const selectedConversationId = current?.conversation_id;
  const isStreaming = processingStreamingChunkStatus;
  const isAddDisabled = data?.[0]?.type === TEMPORARY || isStreaming;

  // Client-side filter over already-loaded conversations (UI-only; no API change).
  const filteredData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return data;
    }
    return data?.filter((item) => item.name?.toLowerCase().includes(term));
  }, [data, searchTerm]);

  // Group already-loaded conversations by date for display headers (UI-only).
  // Search filter still applies because we bucket `filteredData`.
  const buckets = useMemo(
    () => groupByDateBucket(filteredData, (item) => item.last_updated),
    [filteredData]
  );

  const bucketLabel = (bucket: { key: string; label: string }) => {
    switch (bucket.key) {
      case 'today':
        return t('chat.group.today', { defaultValue: 'Today' });
      case 'yesterday':
        return t('chat.group.yesterday', { defaultValue: 'Yesterday' });
      case 'previous7days':
        return t('chat.group.previous7days', { defaultValue: 'Previous 7 days' });
      case 'older':
        return t('chat.group.older', { defaultValue: 'Older' });
      default:
        return bucket.label;
    }
  };

  const onShowMore = () => {
    dispatch(
      conversationAction.getConversations({
        page: current_page + 1,
        size: 10,
      })
    );
  };

  const onClickDelete = (conversation: Conversation) => {
    setRecordDelete(conversation);
    onOpenDeleteDialog();
  };

  const onClickEdit = (conversation: Conversation) => {
    setRecordEdit(conversation);
    onOpenEditDialog();
  };

  const handleDelete = (conversation: Conversation) => {
    dispatch(
      conversationAction.deleteConversation({
        id: conversation.id,
      })
    );
  };

  const handleEdit = (updatedConversation: Conversation & { new_name: string }) => {
    dispatch(
      conversationAction.editConversationName({
        id: updatedConversation.id,
        new_name: updatedConversation.new_name,
      })
    );
  };

  useEffect(() => {
    if (current?.conversation_id) {
      dispatch(conversationAction.updateViewMode(VIEW_MODE.CHAT));
      dispatch(conversationAction.getDetailConversation(current));
    }
  }, [current?.conversation_id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <DeleteModal
        isOpen={isOpenDeleteDialog}
        onClose={onCloseDeleteDialog}
        recordDelete={recordDelete}
        action={handleDelete}
        dataHeader={t('chat.deleteConversation')}
        dataBody={t('chat.deleteConversationInstruction')}
        btnAction={t('common.delete')}
        color='red'
      />
      <EditModal
        isOpen={isOpenEditDialog}
        onClose={onCloseEditDialog}
        recordEdit={recordEdit}
        action={handleEdit}
        dataHeader={t('chat.renameConversation')}
        btnAction={t('chat.rename')}
      />

      {/* DS conversation panel — surface, border, ds_sm shadow (design-guidelines.md §4) */}
      <Flex
        pos='relative'
        direction='column'
        overflow='hidden'
        // Match the Translator panel width exactly: it uses a repeat(12,1fr) grid with
        // the panel at colSpan 3, so width = (content - 11*gap)/12*3 + 2*gap where
        // content = 100vw - sidebar(264px) and gap = 16px → (100vw - 440px)/4 + 32px.
        // Proportional (not fixed px) so it tracks Translator on every screen size. (#78760)
        w={{ base: '100%', lg: isShow ? 'calc((100vw - 440px) / 4 + 32px)' : '52px' }}
        minW={{ base: '100%', lg: isShow ? 'calc((100vw - 440px) / 4 + 32px)' : '52px' }}
        maxW={{ lg: isShow ? 'calc((100vw - 440px) / 4 + 32px)' : '52px' }}
        h='100%'
        minH={0}
        bg={{ lg: 'bg.surface' }}
        borderWidth={{ lg: '1px' }}
        borderColor={{ lg: 'border.default' }}
        borderRadius={{ lg: 'lg' }}
        shadow={{ lg: isShow ? 'ds_sm' : 'none' }}
        transition='width .12s ease-out'
      >
        {/* Panel title (expanded only) — matches Summary "Summaries" / Translator "Jobs" + mockup-v2. */}
        {isShow && (
          <Text
            px={4}
            pt={4}
            color='text.default'
            fontFamily='heading'
            fontSize='md'
            fontWeight={600}
          >
            {t('common.conversations', { defaultValue: 'Conversations' })}
          </Text>
        )}
        {/* Header: New chat (collapses to icon) + collapse toggle.
            Collapsed → stack vertically so the expand toggle stays visible at 52px
            (was clipped when both icon buttons sat in a row). */}
        <Flex
          align='center'
          direction={isShow ? 'row' : 'column'}
          flexShrink={0}
          gap={2}
          px={isShow ? 4 : 1}
          pt={isShow ? 2 : 4}
          pb={isShow ? 3 : 2}
        >
          {isShow ? (
            <Button
              flex={1}
              h='40px'
              isDisabled={isAddDisabled}
              leftIcon={<Plus size={17} />}
              onClick={onAddNew}
              size='ds-md'
              variant='ds-primary'
            >
              {t('common.newChat')}
            </Button>
          ) : (
            <Tooltip label={t('common.newChat')} placement='right'>
              <IconButton
                aria-label={t('common.newChat')}
                icon={<Plus size={19} />}
                isDisabled={isAddDisabled}
                onClick={onAddNew}
                size='ds-md'
                variant='ds-soft'
              />
            </Tooltip>
          )}
          <Tooltip
            display={{ base: 'none', lg: 'block' }}
            label={isShow ? t('common.close') : t('common.open')}
            placement='right'
          >
            <IconButton
              display={{ base: 'none', lg: 'inline-flex' }}
              aria-label={isShow ? t('common.close') : t('common.open')}
              icon={isShow ? <PanelLeftClose size={18} /> : <PanelLeftOpen size={18} />}
              isDisabled={isShowCitation}
              onClick={onToggleSidebar}
              size='ds-md'
              variant='ds-ghost'
            />
          </Tooltip>
        </Flex>

        {/* Search conversations (expanded only) */}
        {isShow && (
          <Box flexShrink={0} px={4} pb={3}>
            <InputGroup size='sm'>
              <InputLeftElement color='text.subtle' pointerEvents='none'>
                <Search size={15} />
              </InputLeftElement>
              <Input
                color='text.default'
                fontSize='sm'
                bg='bg.canvas'
                borderColor='border.default'
                borderRadius='md'
                _hover={{ borderColor: 'border.strong' }}
                _focusVisible={{
                  borderColor: 'primary.solid',
                  boxShadow: '0 0 0 1px var(--chakra-colors-primary-solid)',
                }}
                _placeholder={{ color: 'text.subtle' }}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('chat.searchConversations', {
                  defaultValue: 'Search conversations',
                })}
                value={searchTerm}
              />
            </InputGroup>
          </Box>
        )}

        {/* List */}
        <Box
          flex={1}
          overflowY={isShow ? 'auto' : 'hidden'}
          px={isShow ? 3 : 0}
          pb={3}
          visibility={isShow ? 'visible' : 'hidden'}
        >
          {buckets.map((bucket) => (
            <Box key={bucket.key}>
              {/* Date-bucket header (TODAY / YESTERDAY / PREVIOUS 7 DAYS / month) */}
              <Text
                px={2}
                pt={2}
                pb={1}
                color='text.subtle'
                fontSize='xs'
                fontWeight={600}
                letterSpacing='0.04em'
                textTransform='uppercase'
              >
                {bucketLabel(bucket)}
              </Text>

              <Flex direction='column' gap='2px'>
                {bucket.items.map((item) => {
                  const isActive = selectedConversationId === item.id;
                  return (
                    <Flex
                      key={item.id}
                      align='flex-start'
                      gap={3}
                      px={3}
                      py={3}
                      bg={isActive ? 'primary.soft' : 'transparent'}
                      borderRadius='md'
                      _hover={{ bg: isActive ? 'primary.soft' : 'bg.canvas' }}
                      cursor={isStreaming ? 'not-allowed' : 'pointer'}
                      transition='background .12s ease-out'
                      onClick={() => {
                        onChange(item);
                        onCloseSidebar();
                      }}
                    >
                      <Box
                        flexShrink={0}
                        mt='2px'
                        color={isActive ? 'primary.solid' : 'text.subtle'}
                      >
                        <MessageSquare size={16} />
                      </Box>
                      <Box flex={1} minW={0}>
                        {(() => {
                          // BE names a conversation from its first message; ones with no message
                          // come back with an empty name — show a fallback so the row isn't blank.
                          const title =
                            item.name?.trim() ||
                            t('chat.untitledConversation', {
                              defaultValue: 'Untitled conversation',
                            });
                          const isUntitled = !item.name?.trim();
                          return (
                            <Tooltip label={title} placement='top-start'>
                              <Text
                                overflow='hidden'
                                color={
                                  isActive
                                    ? 'primary.solid'
                                    : isUntitled
                                      ? 'text.subtle'
                                      : 'text.default'
                                }
                                fontSize='sm'
                                fontWeight={isActive ? 600 : 500}
                                fontStyle={isUntitled ? 'italic' : 'normal'}
                                whiteSpace='nowrap'
                                textOverflow='ellipsis'
                              >
                                {title}
                              </Text>
                            </Tooltip>
                          );
                        })()}
                        <Text mt='2px' color='text.subtle' fontSize='xs'>
                          {relativeTimeFormat(item?.last_updated)}
                        </Text>
                      </Box>
                      <ConversationOptions
                        data={item}
                        onClickDelete={onClickDelete}
                        onClickEdit={onClickEdit}
                      />
                    </Flex>
                  );
                })}
              </Flex>
            </Box>
          ))}

          {current_page + 1 < total_page && (
            <Button
              w='100%'
              mt={2}
              fontWeight={500}
              onClick={onShowMore}
              size='ds-sm'
              variant='ds-ghost'
            >
              {t('common.moreHistory')}
            </Button>
          )}
        </Box>
      </Flex>
    </>
  );
};

interface ConversationOptionsProps {
  data: Conversation;
  onClickDelete: (_data: Conversation) => void;
  onClickEdit: (_data: Conversation) => void;
}

const ConversationOptions = ({ data, onClickDelete, onClickEdit }: ConversationOptionsProps) => {
  const { t } = useTranslation();
  return (
    <Menu placement='bottom-end'>
      <MenuButton
        as={IconButton}
        flexShrink={0}
        aria-label={t('common.edit')}
        icon={<MoreHorizontal size={16} />}
        onClick={(e) => {
          e.stopPropagation();
        }}
        size='ds-sm'
        variant='ds-ghost'
      />
      <MenuList minW='140px' bg='bg.surfaceRaised' borderColor='border.default' shadow='ds_lg'>
        {data?.type !== TEMPORARY && (
          <MenuItem
            color='text.default'
            fontSize='sm'
            bg='transparent'
            _hover={{ bg: 'primary.soft', color: 'primary.solid' }}
            onClick={(e) => {
              e.stopPropagation();
              onClickEdit(data);
            }}
          >
            {t('common.edit')}
          </MenuItem>
        )}
        <MenuItem
          color='state.error'
          fontSize='sm'
          bg='transparent'
          _hover={{ bg: 'state.errorSoft' }}
          onClick={(e) => {
            e.stopPropagation();
            onClickDelete(data);
          }}
        >
          {t('common.delete')}
        </MenuItem>
      </MenuList>
    </Menu>
  );
};

export default ConversationsList;
